import {
  alpha,
  Components,
  iconButtonClasses,
  IconButtonProps,
  Theme,
  touchRippleClasses,
} from '../../material';

// Mirrors `MuiButton`'s outlined/contained styles. The `default` color is
// treated like MUI Button's `inherit` (no `default` color exists on Button),
// so outlined falls back to `currentColor` and contained falls back to a
// neutral grey.
//
// Square shape and dimensions per design:
//   small  : 24x24, padding 4
//   medium : 36x36, padding 8
//   large  : 48x48, padding 12
// For `outlined`, padding is reduced by 1px to compensate for the border so
// the visible content area matches the `text` variant.

type SizeKey = NonNullable<IconButtonProps['size']>;

const SIZE_RULES: Record<SizeKey, { dimension: number; padding: number }> = {
  small: { dimension: 24, padding: 4 },
  medium: { dimension: 36, padding: 8 },
  large: { dimension: 48, padding: 12 },
};

type PaletteColorKey = Exclude<
  NonNullable<IconButtonProps['color']>,
  'inherit' | 'default'
>;

const resolvePaletteColor = (theme: Theme, color: IconButtonProps['color']) =>
  !color || color === 'inherit' || color === 'default'
    ? null
    : theme.palette[color as PaletteColorKey];

export const MuiIconButton: Components<Theme>['MuiIconButton'] = {
  defaultProps: {
    variant: 'text',
  },
  styleOverrides: {
    root: ({ theme, ownerState }) => {
      const size = (ownerState.size ?? 'medium') as SizeKey;
      const { dimension, padding } = SIZE_RULES[size] ?? SIZE_RULES.medium;
      const variant = ownerState.variant ?? 'text';
      const palette = resolvePaletteColor(theme, ownerState.color);

      const base = {
        width: dimension,
        height: dimension,
        padding,
        borderRadius: theme.shape.borderRadius,
        // Make the click ripple match the rounded-square shape instead of
        // MUI's default circular bubble. Selector includes both `ripple` and
        // `child` to outweigh MUI's internal `.MuiTouchRipple-ripple .child`
        // rule that hardcodes `border-radius: 50%`.
        [`& .${touchRippleClasses.ripple} .${touchRippleClasses.child}`]: {
          borderRadius: theme.shape.borderRadius,
        },
      };

      if (variant === 'outlined') {
        const main = palette?.main;
        const hoverBg = main
          ? alpha(main, theme.palette.action.hoverOpacity)
          : alpha(
              theme.palette.text.primary,
              theme.palette.action.hoverOpacity
            );

        return {
          ...base,
          // Compensate for 1px border so the outer dimensions match `text`.
          padding: padding - 1,
          border: `1px solid ${main ? alpha(main, 0.5) : 'currentColor'}`,
          color: main ?? 'inherit',
          '@media (hover: hover)': {
            '&:hover': {
              borderColor: main ?? 'currentColor',
              backgroundColor: hoverBg,
            },
          },
          [`&.${iconButtonClasses.disabled}`]: {
            border: `1px solid ${theme.palette.action.disabledBackground}`,
          },
        };
      }

      if (variant === 'contained') {
        const bg = palette?.main ?? theme.palette.grey[300];
        const hoverBg = palette?.dark ?? theme.palette.grey[400];
        // For `default`/`inherit`, derive a readable icon color from the bg
        // luminance instead of relying on `text.primary` (white in dark mode).
        const color =
          palette?.contrastText ?? theme.palette.getContrastText(bg);

        return {
          ...base,
          backgroundColor: bg,
          color,
          // Match `MuiButton` override: no elevation on contained.
          boxShadow: 'none',
          '@media (hover: hover)': {
            '&:hover': {
              backgroundColor: hoverBg,
              boxShadow: 'none',
            },
          },
          [`&.${iconButtonClasses.disabled}`]: {
            color: theme.palette.action.disabled,
            backgroundColor: theme.palette.action.disabledBackground,
          },
        };
      }

      return base;
    },
  },
};
